import { Navigate } from 'react-router-dom'
import { MemberPortalBilling } from '@/components/member-portal/MemberPortalBilling'
import { MemberPortalEmailPreferences } from '@/components/member-portal/MemberPortalEmailPreferences'
import {
  MemberPortalEventOrder,
  MemberPortalEvents,
} from '@/components/member-portal/MemberPortalEvents'
import { MemberPortalInvoiceDetail } from '@/components/member-portal/MemberPortalInvoiceDetail'
import { MemberPortalMembership as MemberPortalMembershipPanel } from '@/components/member-portal/MemberPortalMembership'
import {
  MemberPortalOverview,
  type MemberPortalMembership,
} from '@/components/member-portal/MemberPortalOverview'
import type { OpenInvoice } from '@/components/member-portal/MemberPortalOpenInvoices'
import type { MemberStatusSource } from '@/components/member-portal/memberPortalPresentation'
import { MemberPortalProfile } from '@/components/member-portal/MemberPortalProfile'
import { MemberPortalGiving } from '@/components/member-portal/MemberPortalGiving'
import { MemberPortalDirectory } from '@/components/member-portal/MemberPortalDirectory'
import { MemberPortalMemberProfile } from '@/components/member-portal/MemberPortalMemberProfile'
import { MemberPortalPrivacy } from '@/components/member-portal/MemberPortalPrivacy'
import { MemberPortalGivingManage } from '@/components/member-portal/MemberPortalGivingManage'
import { MemberPortalDirectoryPreview } from '@/components/member-portal/MemberPortalDirectoryPreview'
import { MemberPortalOrganizationPage } from '@/components/member-portal/MemberPortalOrganizationPage'
import type { MemberPortalOrganization } from '@/components/member-portal/MemberSignIn'
import { MemberPortalLeaderRoster } from '@/components/member-portal/MemberPortalLeaderRoster'
import { MemberPortalLeaderCompose } from '@/components/member-portal/MemberPortalLeaderCompose'
import { MemberPortalMyLearning } from '@/components/member-portal/MemberPortalMyLearning'

export function MemberPortalContent({
  path,
  associationName,
  membership,
  memberAccess,
  person,
  openInvoices,
  currency,
  memberStatusSources,
  organizations,
  leaderChapters,
  blastPaused,
  onOrganizationChange,
}: {
  path: string
  associationName: string
  membership?: MemberPortalMembership | null
  memberAccess?: boolean
  person?: { name: string; email: string; emailSuppressed?: boolean }
  openInvoices?: OpenInvoice[]
  currency?: string
  memberStatusSources?: MemberStatusSource[]
  organizations?: MemberPortalOrganization[]
  leaderChapters?: { id: number; name: string; archived: boolean }[]
  blastPaused?: boolean
  onOrganizationChange?: (organizationId: number) => void
}) {
  const effectiveMemberAccess = memberAccess ?? Boolean(membership)
  const leaderChapterId =
    portalLeaderChapterId(path) ??
    (path === '/portal/leader/chapters'
      ? (leaderChapters?.find((chapter) => !chapter.archived)?.id ??
        leaderChapters?.[0]?.id ??
        null)
      : null)
  const leaderContent = leaderPortalContent(
    path,
    leaderChapterId,
    leaderChapters,
    associationName,
    blastPaused,
  )
  if (leaderContent) return leaderContent
  return portalContentForPath({
    path,
    associationName,
    membership,
    memberAccess: effectiveMemberAccess,
    person,
    openInvoices,
    currency,
    memberStatusSources,
    organizations,
    onOrganizationChange,
  })
}

function leaderPortalContent(
  path: string,
  chapterId: number | null,
  chapters: { id: number; name: string; archived: boolean }[] | undefined,
  associationName: string,
  blastPaused: boolean | undefined,
) {
  if (!chapterId) return null
  if (path.endsWith('/email')) {
    const chapter = chapters?.find((item) => item.id === chapterId)
    if (chapter) {
      return (
        <MemberPortalLeaderCompose
          chapter={chapter}
          associationName={associationName}
          blastPaused={blastPaused}
        />
      )
    }
  }
  return <MemberPortalLeaderRoster chapterId={chapterId} chapters={chapters ?? []} />
}

function portalContentForPath({
  path,
  associationName,
  membership,
  memberAccess,
  person,
  openInvoices,
  currency,
  memberStatusSources,
  organizations,
  onOrganizationChange,
}: Omit<Parameters<typeof MemberPortalContent>[0], 'memberAccess'> & { memberAccess: boolean }) {
  const invoiceId = portalInvoiceId(path)
  const eventOrderId = portalEventOrderId(path)
  const directoryMemberSlug = portalDirectoryMemberSlug(path)
  const organizationId = portalOrganizationId(path)

  const exactContent = {
    '/portal/profile': <MemberPortalProfile />,
    '/portal/profile/directory': <MemberPortalPrivacy />,
    '/portal/profile/directory/preview': <MemberPortalDirectoryPreview />,
    '/portal/directory': <MemberPortalDirectory />,
    '/portal/billing': <MemberPortalBilling organizations={organizations} />,
    '/portal/email-preferences': <MemberPortalEmailPreferences />,
    '/portal/giving': <MemberPortalGiving />,
    '/portal/giving/manage': <MemberPortalGivingManage />,
    '/portal/events': <MemberPortalEvents />,
    '/portal/my-learning': <MemberPortalMyLearning />,
  }[path as string]
  if (exactContent) return exactContent

  if (directoryMemberSlug) return <MemberPortalMemberProfile slug={directoryMemberSlug} />
  if (invoiceId) return <MemberPortalInvoiceDetail invoiceId={invoiceId} />
  if (path === '/portal/membership') {
    return (
      <MemberPortalMembershipPanel
        associationName={associationName}
        membership={membership}
        memberAccess={memberAccess}
        memberStatusSources={memberStatusSources}
      />
    )
  }
  if (eventOrderId) return <MemberPortalEventOrder orderId={eventOrderId} />
  if (organizationId || path === '/portal/organization') {
    const sortedOrganizations = [...(organizations ?? [])].sort((left, right) =>
      left.name.localeCompare(right.name),
    )
    const organization = organizationId
      ? sortedOrganizations.find((item) => item.id === organizationId)
      : sortedOrganizations[0]
    if (!organization) return <Navigate to="/portal" replace />
    if (organization) {
      return (
        <MemberPortalOrganizationPage
          organization={organization}
          organizations={sortedOrganizations}
          onOrganizationChange={onOrganizationChange}
        />
      )
    }
  }

  return (
    <MemberPortalOverview
      associationName={associationName}
      membership={membership}
      person={person}
      openInvoices={openInvoices}
      currency={currency}
      memberStatusSources={memberStatusSources}
    />
  )
}

export function memberPortalDestinationPath(path: string) {
  if (portalInvoiceId(path)) return '/portal/billing'
  if (portalEventOrderId(path)) return '/portal/events'
  if (portalDirectoryMemberSlug(path)) return '/portal/directory'
  if (path === '/portal/profile/directory') return '/portal/profile'
  if (path === '/portal/giving/manage') return '/portal/giving'
  if (portalOrganizationId(path)) return '/portal/organization'
  if (portalLeaderChapterId(path)) return '/portal/leader/chapters'
  return path
}

function portalLeaderChapterId(path: string) {
  const match = path.match(/^\/portal\/leader\/chapters\/(\d+)(?:\/email)?$/)
  return match ? Number(match[1]) : null
}

export function memberPortalDetailPath(path: string) {
  return Boolean(
    portalInvoiceId(path) ||
    portalEventOrderId(path) ||
    portalDirectoryMemberSlug(path) !== null ||
    path === '/portal/profile/directory' ||
    path === '/portal/profile/directory/preview' ||
    path === '/portal/giving/manage' ||
    portalLeaderChapterId(path) !== null ||
    path === '/portal/leader/chapters' ||
    path === '/portal/organization' ||
    /^\/portal\/organization\/\d+$/.test(path),
  )
}

function portalOrganizationId(path: string) {
  const match = path.match(/^\/portal\/organization\/(\d+)$/)
  return match ? Number(match[1]) : null
}

function portalInvoiceId(path: string) {
  const match = path.match(/^\/portal\/billing\/invoices\/(\d+)$/)
  return match ? Number(match[1]) : null
}

function portalEventOrderId(path: string) {
  const match = path.match(/^\/portal\/events\/(\d+)$/)
  return match ? Number(match[1]) : null
}

export function portalDirectoryMemberSlug(path: string) {
  const match = path.match(/^\/portal\/directory\/([^/]+)$/)
  return match ? match[1] : null
}
